'use client';

import { motion } from 'motion/react';
import { useState, useRef } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

const floorPlans = [
  {
    id: 1,
    name: '1 Bedroom',
    images: [
      'images/gallery/floor-plans/1BHK - A1.jpg',
      'images/gallery/floor-plans/1BHK - A2.jpg',
      'images/gallery/floor-plans/1BHK - B1.jpg',
      'images/gallery/floor-plans/1BHK - B2.jpg',
      'images/gallery/floor-plans/1BHK - C1.jpg',
      'images/gallery/floor-plans/1BHK - C2.jpg',
      'images/gallery/floor-plans/1BHK - C3.jpg',
    ],
  },
  {
    id: 2,
    name: '1 Bedroom Plus',
    images: [
      'images/gallery/floor-plans/1BHK PLUS - A1.jpg',
      'images/gallery/floor-plans/1BHK PLUS - A2.jpg',
      'images/gallery/floor-plans/1BHK PLUS - A3.jpg',
      'images/gallery/floor-plans/1BHK PLUS - A4.jpg',
      'images/gallery/floor-plans/1BHK PLUS - B.jpg',
    ],
  },
  {
    id: 3,
    name: '2 Bedroom Plus JR',
    images: [
      'images/gallery/floor-plans/2BHK PLUS JR.jpg',
    ],
  },
  {
    id: 4,
    name: '2 Bedroom Plus SR',
    images: [
      'images/gallery/floor-plans/2BHK PLUS SR - A1.jpg',
      'images/gallery/floor-plans/2BHK PLUS SR - A2.jpg',
      'images/gallery/floor-plans/2BHK PLUS SR - B.jpg',
      'images/gallery/floor-plans/2BHK PLUS SR - C.jpg',
      'images/gallery/floor-plans/2BHK PLUS SR - D.jpg',
      'images/gallery/floor-plans/2BHK PLUS SR - E.jpg',
    ],
  },
];

export function FloorPlanSection() {
  const [selectedPlan, setSelectedPlan] = useState(floorPlans[0]);
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <section
      id="floorplans"
      className="relative py-24 md:py-32"
      style={{ backgroundImage: "url('/images/gallery/bg-2-new.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-white/25 z-0" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-[1px] bg-[#8a6b3f] w-[80px] mx-auto mb-8 origin-center"
          />
          <h2 className="text-[#8a6b3f] tracking-[0.15em] font-light text-xl md:text-4xl">FLOOR PLANS</h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Mobile - Horizontal Tabs */}
          <div className="w-full lg:hidden overflow-x-auto">
            <div className="flex gap-3 pb-4 min-w-max px-1">
              {floorPlans.map((plan, index) => (
                <motion.button
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => setSelectedPlan(plan)}
                  className={`flex-shrink-0 px-6 py-4 border transition-all duration-300 cursor-pointer ${selectedPlan.id === plan.id
                    ? 'bg-[#8a6b3f]/10 border-[#8a6b3f]/50'
                    : 'bg-[#8a6b3f]/5 border-[#8a6b3f]/20'
                    }`}
                >
                  <h3 className="text-[#8a6b3f] text-sm tracking-[0.1em] font-bold whitespace-nowrap">
                    {plan.name}
                  </h3>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Desktop - Left Side Options */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="hidden lg:block w-full lg:w-1/2 space-y-4"
          >
            {floorPlans.map((plan, index) => (
              <motion.button
                key={plan.id}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setSelectedPlan(plan)}
                className={`w-full text-left p-6 border transition-all duration-300 cursor-pointer ${selectedPlan.id === plan.id
                  ? 'bg-[#8a6b3f]/20 border-[#8a6b3f]/50'
                  : 'bg-[#8a6b3f]/10 border-[#8a6b3f]/20 hover:bg-[#8a6b3f]/15 hover:border-[#8a6b3f]/30'
                  }`}
              >
                <h3 className="text-[#8a6b3f] text-xl md:text-xl tracking-[0.1em] font-light">
                  {plan.name}
                </h3>
              </motion.button>
            ))}
          </motion.div>

          {/* Right Side - Image Swiper */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2"
          >
            <div className="bg-[#8a6b3f]/5 border border-[#8a6b3f]/20 p-4">
              <div className="relative">
                <Swiper
                  modules={[Pagination]}
                  pagination={{ clickable: true }}
                  spaceBetween={20}
                  className="floor-plan-swiper"
                  key={selectedPlan.id}
                  onSwiper={(swiper) => { swiperRef.current = swiper; }}
                >
                  {selectedPlan.images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="aspect-[4/3] bg-[#8a6b3f]/5 relative">
                        <Image
                          src={`/${image}`}
                          alt={`${selectedPlan.name} - View ${index + 1}`}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, 80vw"
                          loading="lazy"
                          quality={80}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                {/* Custom Navigation Buttons */}
                <button
                  onClick={() => swiperRef.current?.slidePrev()}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-[#8a6b3f]/20 hover:bg-[#8a6b3f]/35 transition-colors shadow-sm"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6 text-[#8a6b3f]" />
                </button>
                <button
                  onClick={() => swiperRef.current?.slideNext()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-[#8a6b3f]/20 hover:bg-[#8a6b3f]/35 transition-colors shadow-sm"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6 text-[#8a6b3f]" />
                </button>
              </div>

              <div className="mt-6 text-center">
                <h4 className="text-[#8a6b3f] text-xl tracking-[0.1em] font-light mb-2">
                  {selectedPlan.name}
                </h4>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}